<script setup lang="ts">
import { computed } from 'vue'
import type { UsageWeek } from '../stores/usage'
import { formatUsd, modelColor, shortModel } from '../composables/usageFormatting'
import { chartAriaLabel, chartWeeks, formatWeekStart } from './UsageSpendBars'

// "Previous weeks" card on /usage — a small column chart of weekly spend
// (oldest left, the live current week highlighted on the right), drawn
// with plain divs. The chart is a single role="img" with a full text
// alternative; the per-week, per-model numbers stay available in the
// collapsible breakdown underneath (frozen UsageReport snapshots).
const props = defineProps<{
  currentWeek: UsageWeek | null
  pastWeeks: UsageWeek[]
}>()

const bars = computed(() => chartWeeks(props.currentWeek, props.pastWeeks))
const ariaLabel = computed(() => chartAriaLabel(bars.value))
const columns = computed(() => ({ gridTemplateColumns: `repeat(${bars.value.length}, minmax(0, 1fr))` }))
</script>

<template>
  <section
    data-testid="past-weeks"
    aria-labelledby="usage-weeks-heading"
    class="flex flex-col gap-3.5 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
  >
    <h2 id="usage-weeks-heading" class="m-0 text-[15px] font-semibold text-ink">Previous weeks</h2>

    <div
      role="img"
      :aria-label="ariaLabel"
      data-testid="usage-weeks-chart"
      class="grid h-[150px] items-end gap-1.5 border-b border-border-subtle sm:gap-2.5"
      :style="columns"
    >
      <span
        v-for="bar in bars"
        :key="bar.weekStart"
        data-testid="usage-weeks-bar"
        class="block rounded-t"
        :class="bar.isCurrent ? 'bg-ink' : 'bg-ink-faint/50'"
        :style="{ height: `${bar.heightPercent}%` }"
      />
    </div>
    <div class="grid gap-1.5 text-center text-[11px] sm:gap-2.5" :style="columns" aria-hidden="true">
      <span
        v-for="bar in bars"
        :key="bar.weekStart"
        :class="bar.isCurrent ? 'font-semibold text-ink' : 'text-ink-faint'"
        >{{ bar.label }}</span
      >
    </div>
    <p v-if="currentWeek" class="m-0 flex items-center gap-2 text-xs text-ink-faint" aria-hidden="true">
      <span class="inline-block size-2 rounded-sm bg-ink" />
      This week so far
      <span class="inline-block size-2 rounded-sm bg-ink-faint/50 ml-2" />
      Previous weeks
    </p>

    <details class="border-t border-border-divider pt-2">
      <summary class="min-h-11 cursor-pointer py-3 text-sm text-ink-muted">Breakdown by week</summary>
      <ul class="m-0 list-none p-0">
        <li
          v-for="week in pastWeeks"
          :key="week.week_start"
          data-testid="usage-week-row"
          class="flex flex-col gap-1 border-t border-border-divider py-2.5"
        >
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-sm text-ink-muted">Week of {{ formatWeekStart(week.week_start) }}</span>
            <span class="font-mono text-xs text-ink tabular-nums">{{ formatUsd(week.total_cost_cents) }}</span>
          </div>
          <div v-if="week.models.length > 0" class="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-faint">
            <span v-for="m in week.models" :key="m.model" class="flex items-center gap-1.5">
              <span class="inline-block size-2 rounded-sm" :class="modelColor(m.model)" />
              <span class="font-mono">{{ shortModel(m.model) }}</span>
              <span class="tabular-nums">· {{ formatUsd(m.estimated_cost_cents) }}</span>
            </span>
          </div>
        </li>
      </ul>
    </details>
  </section>
</template>
